import { lazy, Suspense, type ReactNode } from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { canAny, useMe, type Capability } from "@/lib/me";
import { useHomePath } from "@/components/TopNav";
import { CrmLayout } from "@/components/layouts/CrmLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Skeleton } from "@/components/ui";
import { DashboardPage } from "@/pages/DashboardPage";
import { ContactsPage } from "@/pages/ContactsPage";
import { FrontDeskPage } from "@/pages/FrontDeskPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { ContactDetailPage } from "@/pages/ContactDetailPage";
import { ListsPage } from "@/pages/ListsPage";
import { InboxPage } from "@/pages/InboxPage";
import { MailPage } from "@/pages/MailPage";
import { AdsPage } from "@/pages/AdsPage";
import { VideoRoomsPage } from "@/pages/VideoRoomsPage";
import { CampaignsPage } from "@/pages/CampaignsPage";
import { CalendarPage } from "@/pages/CalendarPage";
import { TasksPage } from "@/pages/TasksPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { PaymentsPage } from "@/pages/PaymentsPage";
import { SchedulingLinksPage } from "@/pages/SchedulingLinksPage";
import { BookingPage } from "@/pages/BookingPage";
import { MedspaBookingPage } from "@/pages/MedspaBookingPage";
import { SchedulePage } from "@/pages/SchedulePage";
import { ServicesPage } from "@/pages/ServicesPage";
import { FormsPage } from "@/pages/FormsPage";
import { TeamPage } from "./pages/TeamPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

// Lazy-load the heavy chart/graph routes so xyflow + dagre (~270 KB) and
// recharts (~110 KB) don't ship to users who never visit those pages.
// Most sessions don't touch /automations or /reports — paying for those
// libraries on every login adds up fast.
const AutomationsPage = lazy(() => import("@/pages/AutomationsPage").then((m) => ({ default: m.AutomationsPage })));
// Lazy: pulls in the WebRTC SDK, which most sessions never need.
const ReportsPage     = lazy(() => import("@/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));

/**
 * A page this person's sections don't include. The server refuses the data
 * anyway; this just says so plainly instead of showing a broken screen.
 */
function Guard({ needs, children }: { needs: Capability | Capability[]; children: ReactNode }) {
  const me = useMe();
  const home = useHomePath();
  if (canAny(me, needs)) return <>{children}</>;
  return (
    <div className="flex min-h-[50vh] items-center justify-center px-4">
      <div className="max-w-sm rounded-2xl border border-zinc-800/70 bg-zinc-900/30 p-8 text-center">
        <Lock className="mx-auto h-6 w-6 text-zinc-500" />
        <h1 className="mt-3 text-base font-semibold text-zinc-100">This part isn&rsquo;t open to you</h1>
        <p className="mt-1.5 text-sm text-zinc-500">Ask the practice owner if you need it. They can switch it on for you in Settings, Staff.</p>
        {home && <Link to={home} className="mt-4 inline-block text-sm font-medium text-indigo-300 hover:text-indigo-200">Go to my start page</Link>}
      </div>
    </div>
  );
}

/** "/" lands on the first section this person can open. */
function HomeRedirect() {
  const home = useHomePath();
  return <Navigate to={home ?? "/frontdesk"} replace />;
}

function PageSpinner() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public booking link — no auth, no CrmLayout chrome. The slug
            scopes which calendar's availability is shown. */}
        <Route path="/book/:slug" element={<BookingPage />} />
        {/* Public med-spa self-booking — service menu → provider → time. */}
        <Route path="/booking" element={<MedspaBookingPage />} />

        {/* Authenticated CRM */}
        <Route element={<CrmLayout />}>
          <Route path="/"                element={<HomeRedirect />} />
          <Route path="/dashboard"       element={<Guard needs="reports"><DashboardPage /></Guard>} />
          <Route path="/frontdesk"       element={<Guard needs="schedule"><FrontDeskPage /></Guard>} />
          <Route path="/register"        element={<Guard needs={["checkout", "reports"]}><RegisterPage /></Guard>} />
          <Route path="/contacts"        element={<Guard needs="clients"><ContactsPage /></Guard>} />
          <Route path="/contacts/:id"    element={<Guard needs="clients"><ContactDetailPage /></Guard>} />
          <Route path="/lists"           element={<Guard needs={["clients", "marketing"]}><ListsPage /></Guard>} />
          <Route path="/inbox"           element={<Guard needs="messages"><InboxPage /></Guard>} />
          <Route path="/inbox/:contactId" element={<Guard needs="messages"><InboxPage /></Guard>} />
          <Route path="/mail"            element={<Guard needs="messages"><MailPage /></Guard>} />
          <Route path="/mail/:threadId"  element={<Guard needs="messages"><MailPage /></Guard>} />
          <Route path="/video-rooms"     element={<Guard needs="schedule"><VideoRoomsPage /></Guard>} />
          {/* Uday's whole login: the ads, and nothing else on the way in. */}
          <Route path="/ads"             element={<Guard needs="ads"><AdsPage /></Guard>} />
          <Route path="/campaigns"       element={<Guard needs="marketing"><CampaignsPage /></Guard>} />
          <Route path="/campaigns/:id"   element={<Guard needs="marketing"><CampaignsPage /></Guard>} />
          <Route path="/schedule"        element={<Guard needs="schedule"><SchedulePage /></Guard>} />
          <Route path="/services"        element={<Guard needs="settings"><ServicesPage /></Guard>} />
          <Route path="/services/:tab"   element={<Guard needs="settings"><ServicesPage /></Guard>} />
          <Route path="/forms"           element={<Guard needs={["settings", "forms"]}><FormsPage /></Guard>} />
          {/* "I can't find settings" — staff and the time clock are things you
              manage, so they also live beside the menu and the forms. */}
          <Route path="/team"            element={<Guard needs={["settings", "staff"]}><TeamPage /></Guard>} />
          <Route path="/checkout"        element={<Guard needs="checkout"><CheckoutPage /></Guard>} />
          <Route path="/calendar"        element={<Guard needs="schedule"><CalendarPage /></Guard>} />
          <Route path="/scheduling-links" element={<Guard needs="schedule"><SchedulingLinksPage /></Guard>} />
          <Route path="/tasks"           element={<Guard needs="clients"><TasksPage /></Guard>} />
          <Route path="/automations"     element={<Guard needs="settings"><Suspense fallback={<PageSpinner />}><AutomationsPage /></Suspense></Guard>} />
          <Route path="/automations/:id" element={<Guard needs="settings"><Suspense fallback={<PageSpinner />}><AutomationsPage /></Suspense></Guard>} />
          <Route path="/reports"         element={<Guard needs="reports"><Suspense fallback={<PageSpinner />}><ReportsPage /></Suspense></Guard>} />
          <Route path="/payments"        element={<Guard needs={["reports", "checkout"]}><PaymentsPage /></Guard>} />
          <Route path="/payments/:tab"   element={<Guard needs="reports"><PaymentsPage /></Guard>} />
          <Route path="/settings"        element={<Guard needs={["settings", "staff"]}><SettingsPage /></Guard>} />
          {/* 404 catch-all — sits inside CrmLayout so the user keeps the
              sidebar nav and can recover. */}
          <Route path="*"                element={<NotFoundPage />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
